import HyattSignIn from 'components/HyattLMS/HyattSignIn';
import HyattSignOut from 'components/HyattLMS/HyattSignOut';
import { useGlobalDateLocale } from 'context/DateLocaleContext';
import { CartIcon } from 'features/ShoppingCart/components/CartIcon/CartIcon';
import useHyattAuth from 'hooks/useHyattAuth';
import useIsHyattLMSEnabledFlag from 'hooks/useIsHyattLMSEnabledFlag';
import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  BackLabel,
  CenterAligned,
  HoverablePrevArrowIcon,
  IconBack,
  LeftAligned,
  LinksBack,
  NavigationHeaderContainer,
  RightAligned,
} from './NavigationHeader.styles';
import { useNavigationBack } from './useNavigationBack';

type NavigationHeaderProps = {
  goBackLabel?: string;
  showCart?: boolean;
  onGoBack?: () => void;
  preventDefaultBack?: boolean;
  className?: string;
  style?: React.CSSProperties;
  /** Hide the default back-arrow link — leaves the left slot empty. */
  hideBackButton?: boolean;
  /** Content rendered in a page-centered middle slot alongside the cart. */
  centerContent?: React.ReactNode;
};

export const NavigationHeader = ({
  goBackLabel,
  showCart = true,
  preventDefaultBack,
  className,
  onGoBack,
  style,
  hideBackButton = false,
  centerContent,
}: NavigationHeaderProps) => {
  const { t: translate } = useTranslation();
  const { isRTL } = useGlobalDateLocale();
  const label = useMemo(
    () => goBackLabel || translate('back'),
    [goBackLabel, translate],
  );
  const goBack = useNavigationBack();

  const hyattLMSEnabled = useIsHyattLMSEnabledFlag();

  const { verifySession } = useHyattAuth();

  const handleBackClick = useCallback(() => {
    onGoBack?.();
    if (!preventDefaultBack) goBack();
  }, [preventDefaultBack, onGoBack, goBack]);

  const renderHyattButton = () => {
    if (!hyattLMSEnabled) return null;
    return verifySession() ? <HyattSignOut /> : <HyattSignIn />;
  };

  return (
    <NavigationHeaderContainer
      $withCenter={Boolean(centerContent)}
      className={className}
      role="navigation"
      style={style}
    >
      <LeftAligned>
        {!hideBackButton && (
          <LinksBack
            aria-label={translate('goBack')}
            className="header__back-button"
            onClick={handleBackClick}
            role="button"
            tabIndex={0}
            title={label}
          >
            <IconBack className="header__back-button-icon">
              <HoverablePrevArrowIcon isRTL={isRTL} />
            </IconBack>
            <BackLabel className="header-back-label">{label}</BackLabel>
          </LinksBack>
        )}
      </LeftAligned>
      {centerContent && <CenterAligned>{centerContent}</CenterAligned>}
      <RightAligned>
        {renderHyattButton()}
        {showCart && <CartIcon />}
      </RightAligned>
    </NavigationHeaderContainer>
  );
};
